/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import {
    Box,
    Button,
    Checkbox,
    Divider,
    Heading,
    Icon,
    NumberField,
    Tooltip,
} from 'sarsaparilla';
import uniq from 'lodash/uniq';
import {
    AMENITIES_TYPE_TRIP_PREFERENCES,
    CAMPING_TYPES,
    ELECTRIC_TYPE,
    KEY_CHARACTERISTICS_TYPE,
    OCCUPANTS_TYPE,
    SEARCH_FILTER_CAMPSITE_ELECTRICAL_REORDERED,
    VEHICLE_LENGTH_TYPE,
} from '../../constants/filters/camping';

type FilterProps = {
    key: string;
    value: string;
    legacyKey: string;
    accessibility?: boolean;
};

type PreferencesListProps = {
    checked: string[];
    preferences: FilterProps[];
    title: string;
    className?: string;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

function PreferencesList({
    checked,
    onChange,
    preferences,
    title,
    className,
}: PreferencesListProps) {
    return (
        <div key={title}>
            <Heading className={className} headingLevel={3} appearance="h6">
                {title}
            </Heading>
            <div className="trip-preferences__list mb-2">
                {preferences.map((item) => {
                    const isChecked = checked.indexOf(item.key) !== -1;
                    return (
                        <div
                            key={item.value}
                            className="trip-preferences__list-item-wrapper"
                        >
                            <Checkbox
                                key={`trip-preferences-${item.key}`}
                                id={`trip-preferences-${item.key}`}
                                className="trip-preferences__list-item"
                                label={item.value}
                                value={item.key}
                                isChecked={isChecked}
                                onChange={onChange}
                            />
                            {item.accessibility && (
                                <span className="search-accessible-camping">
                                    <Tooltip content="Accessible Site: This site has features that provide better accessibility than others.  Such features may include clear ground space, a firm surface and other features.  We are in the process of examining features that improve accessibility and welcome your feedback.">
                                        <Button
                                            appearance="subtle"
                                            size="sm"
                                            iconBeforeElement={<Icon iconName="info" />}
                                            screenReaderTextBefore="Show accessible campsite filter help text"
                                        />
                                    </Tooltip>
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default function TripPreferencesForm({
    checked,
    onChange,
}: {
    onChange: (v: string[]) => void;
    checked: string[];
}) {
    const getNumberValue = (type: string) => {
        const numberValue = checked.find((item) => item.includes(type));
        if (numberValue) {
            const values = numberValue.split(':');
            return Number(values[1]);
        }
        return 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const name = e?.target?.value;
        const isChecked = e?.target?.checked;
        let parsed = uniq([...checked, name]);
        if (!isChecked) {
            parsed = parsed.filter((v) => v !== name);
        }
        onChange(parsed);
    };

    const onNumberChange = (v: number, key: string) => {
        const index = checked.findIndex((item: string) => item.includes(key));
        let list = [...checked];
        if (index !== -1) {
            list.splice(index, 1);
        }
        const param = `${key}:${v}`;
        list.push(param);

        if (v === 0) {
            list = list.filter((item) => !item.includes(key));
        }

        onChange(list);
    };

    const groupSizeValue = getNumberValue(OCCUPANTS_TYPE.key);
    const vehicleLengthValue = getNumberValue(VEHICLE_LENGTH_TYPE.key);
    return (
        <>
            <div>
                <Heading headingLevel={2} appearance="h5">
                    1. Select all that will work for you:
                </Heading>
                <Box border="gray" padding="md" marginY="md" background="gray-1">
                    <PreferencesList
                        title="Camping Type"
                        preferences={CAMPING_TYPES}
                        onChange={handleChange}
                        checked={checked}
                        className="mb-2"
                    />
                    <Divider />
                    <div className="trip-preferences__list my-2">
                        <NumberField
                            units="feet"
                            unitAfter="ft"
                            visualLabel={VEHICLE_LENGTH_TYPE.value}
                            min={0}
                            max={99}
                            value={vehicleLengthValue}
                            onChange={(v) => onNumberChange(v, VEHICLE_LENGTH_TYPE.key)}
                            shouldTriggerOnChange
                        />
                    </div>
                    <Divider />
                    <PreferencesList
                        title="Electricity"
                        preferences={ELECTRIC_TYPE}
                        onChange={handleChange}
                        checked={checked}
                        className="mb-2 mt-4"
                    />
                </Box>
            </div>
            <div className="mt-4 mb-2">
                <Heading headingLevel={2} appearance="h5">
                    2. Narrow your requirements. What are your must-haves?
                </Heading>
                <Box border="gray" padding="md" marginY="md" background="gray-1">
                    <PreferencesList
                        title="Amenities"
                        preferences={AMENITIES_TYPE_TRIP_PREFERENCES}
                        onChange={handleChange}
                        checked={checked}
                        className="mb-2"
                    />
                    <Divider />
                    <PreferencesList
                        title="Electrical Hookup"
                        preferences={SEARCH_FILTER_CAMPSITE_ELECTRICAL_REORDERED}
                        onChange={handleChange}
                        checked={checked}
                        className="mt-4 mb-2"
                    />
                    <Divider />
                    <PreferencesList
                        title="Key Characteristics"
                        preferences={KEY_CHARACTERISTICS_TYPE}
                        onChange={handleChange}
                        checked={checked}
                        className="mt-4 mb-2"
                    />
                    <Divider />
                    <div className="trip-preferences__list my-2">
                        <NumberField
                            units="guests"
                            visualLabel={OCCUPANTS_TYPE.value}
                            min={0}
                            max={99}
                            value={groupSizeValue}
                            onChange={(v) => onNumberChange(v, OCCUPANTS_TYPE.key)}
                            shouldTriggerOnChange
                        />
                    </div>
                </Box>
            </div>
        </>
    );
}
